import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Mail, Activity } from "lucide-react";
import { Toaster, toast } from "sonner";
import { api } from "@/lib/api";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState({ status: "loading", message: "" });
  const [resending, setResending] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const didRun = useRef(false);

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;
    if (!token) {
      setState({ status: "error", message: "No verification token in URL." });
      return;
    }
    api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then((r) => {
        // Any 2xx with success:true is a GREEN state — includes the idempotent
        // replay path where the backend returns {success:true, idempotent:true}
        // for a recently-consumed token (StrictMode double-fire, hard refresh,
        // back button). We do NOT filter on the idempotent flag.
        if (r?.data?.success) {
          setState({
            status: "ok",
            message: r.data.message || "Email verified! You can now sign in.",
            email: r.data.email,
          });
        } else {
          setState({
            status: "error",
            message: r?.data?.detail || "Verification failed.",
          });
        }
      })
      .catch((e) => setState({
        status: "error",
        message: e.response?.data?.detail || "Verification link is invalid or has expired.",
      }));
  }, [token]);

  const resend = async () => {
    if (!resendEmail.includes("@")) { toast.error("Enter your email"); return; }
    setResending(true);
    try {
      const r = await api.post("/auth/resend-verification", { email: resendEmail.trim() });
      toast.success(r.data.message || "Sent");
      setCooldown(60);
      const t = setInterval(() => setCooldown((c) => c <= 1 ? (clearInterval(t), 0) : c - 1), 1000);
      if (r.data.dev_mode_verify_url) {
        toast.info("Dev mode — check console for new URL");
        console.log("[verify-email] DEV URL:", r.data.dev_mode_verify_url);
      }
    } catch (e) { toast.error("Failed to resend"); }
    finally { setResending(false); }
  };

  const isOk = state.status === "ok";
  const isErr = state.status === "error";

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0e1a] px-4" data-testid="verify-email-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 justify-center mb-6">
          <Activity className="w-5 h-5 text-[#3b82f6]" />
          <div className="font-heading font-black tracking-tighter text-xl">VEEROX INTEL AI</div>
        </div>

        <div className="rounded-lg border border-[#1f2937] bg-[#111827] p-6 space-y-5">
          {state.status === "loading" && (
            <div className="flex flex-col items-center py-8 gap-4" data-testid="verify-loading">
              <Loader2 className="w-8 h-8 text-[#3b82f6] animate-spin" />
              <div className="text-sm text-[#9ca3af]">Verifying your email…</div>
            </div>
          )}

          {isOk && (
            <div className="flex flex-col items-center py-4 gap-4" data-testid="verify-success">
              <div className="w-16 h-16 rounded-full bg-[#10b981]/10 border-2 border-[#10b981] flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-[#10b981]" />
              </div>
              <div className="text-center">
                <div className="font-heading font-black text-xl mb-1">Email verified!</div>
                <div className="text-xs text-[#9ca3af] font-mono">
                  Your account is now active. You can sign in below.
                </div>
                {state.email && (
                  <div className="mt-1 text-xs text-[#e5e7eb] font-mono">{state.email}</div>
                )}
              </div>
              <Link
                to="/login?verified=1"
                data-testid="go-to-login-btn"
                className="w-full h-11 rounded-md bg-[#3b82f6] hover:bg-[#2563eb] font-semibold text-sm flex items-center justify-center transition-colors"
              >
                Sign in
              </Link>
            </div>
          )}

          {isErr && (
            <div className="space-y-4" data-testid="verify-error">
              <div className="flex flex-col items-center py-2 gap-3">
                <div className="w-16 h-16 rounded-full bg-[#ef4444]/10 border-2 border-[#ef4444] flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-[#ef4444]" />
                </div>
                <div className="text-center">
                  <div className="font-heading font-black text-xl mb-1">Verification failed</div>
                  <div className="text-xs text-[#9ca3af] font-mono">{state.message}</div>
                </div>
              </div>
              <div className="pt-2 border-t border-[#1f2937]">
                <div className="vx-label mb-2 flex items-center gap-1.5">
                  <Mail className="w-3 h-3" /> Get a new verification link
                </div>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  className="vx-input mb-2"
                  data-testid="verify-resend-email-input"
                />
                <button
                  onClick={resend}
                  disabled={resending || cooldown > 0}
                  className="w-full h-10 rounded-md border border-[#3b82f6]/40 text-[#3b82f6] hover:bg-[#3b82f6]/10 text-xs font-mono uppercase tracking-widest disabled:opacity-40"
                  data-testid="verify-resend-btn"
                >
                  {cooldown > 0 ? `Resend in ${cooldown}s` : resending ? "Sending…" : "Send new link"}
                </button>
              </div>
              <Link to="/login" className="block text-center text-xs text-[#9ca3af] hover:text-[#3b82f6]">
                ← Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
      <Toaster theme="dark" position="top-right" />
    </div>
  );
}
